import { Component, DestroyRef, ElementRef, HostListener, OnInit, computed, effect, inject, signal, viewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import swal from "sweetalert2";

import { ajustePaginado, cuentaEstable, textoRango } from "src/app/calidad-codigo/cc-paginado";
import {
  MantenimientoModulosRes,
  ModuloEnMantenimiento,
  ModuloOpcion,
  MovimientoMantenimiento,
} from "src/app/shared/model/mantenimiento-modulos/mantenimiento-modulos.model";
import { EstadoMantenimientoService } from "../estado-mantenimiento.service";
import { MantenimientoModulosService } from "../mantenimiento-modulos.service";
import { MENSAJE_POR_OMISION, textoHasta } from "../reglas";
import { abrirDialogoMantenimiento } from "./dialogo-mantenimiento.component";

/** Un renglón de la tabla: lo que se puede poner en mantenimiento y, si lo está, cómo. */
export interface FilaMantenimiento {
  opcion: ModuloOpcion;
  activo?: ModuloEnMantenimiento;
}

export type FiltroEstado = "todos" | "mantenimiento" | "disponibles";
type Pestana = "modulos" | "bitacora";

/** Lo que dice la bitácora de cada movimiento. */
const ACCIONES: Record<MovimientoMantenimiento["accion"], string> = {
  INICIA: "Lo puso en mantenimiento",
  EDITA: "Cambió el aviso",
  TERMINA: "Lo terminó",
  VENCE: "Terminó solo: llegó la fecha",
};

/** Las filas por página son las que caben en la ventana, entre estas dos. */
const FILAS_MIN = 4;
const FILAS_MAX = 25;
/** El alto de una fila mientras no hay ninguna pintada para medir. */
const ALTO_FILA = 56;
/** El aire bajo la tarjeta (su borde y el relleno de la página), en píxeles. */
const MARGEN_INFERIOR = 36;

/** Las partes de una fecha «AAAA-MM-DD HH:mm» que manda el servidor, o null. */
function partesDe(fecha: string | null | undefined): RegExpExecArray | null {
  return /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(fecha || "");
}

/** Sin acentos y en minúsculas, para buscar. */
function sinAcentos(texto: string | null | undefined): string {
  return (texto || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

/**
 * Configuración > Mantenimiento de módulos (16/10/2026, pedido de Norman):
 * cada pantalla del menú del COS y las tarjetas de ticket de Solicitudes, con
 * el botón para ponerlas en mantenimiento, cambiarles el aviso o terminarlo.
 * Quien entra a algo en mantenimiento ve el aviso y no pasa, tampoco quien
 * tiene esta pantalla: lo termina aquí. En otra pestaña, la bitácora: quién lo hizo
 * y cuándo. Ninguna de las dos rueda: las filas por página son las que caben
 * en la ventana (cc-paginado.ts, como en Calidad de código).
 */
@Component({
  selector: "app-mantenimiento-modulos",
  imports: [FormsModule, MatFormFieldModule, MatInputModule, MatSelectModule],
  templateUrl: "./mantenimiento-modulos.component.html",
  styleUrl: "./mantenimiento-modulos.component.css",
})
export class MantenimientoModulosComponent implements OnInit {
  private readonly api = inject(MantenimientoModulosService);
  private readonly estado = inject(EstadoMantenimientoService);
  private readonly dialog = inject(MatDialog);
  private readonly tabla = viewChild<ElementRef<HTMLTableElement>>("tabla");

  readonly porOmision = MENSAJE_POR_OMISION;

  readonly opciones = signal<ModuloOpcion[]>([]);
  readonly activos = signal<ModuloEnMantenimiento[]>([]);
  readonly bitacora = signal<MovimientoMantenimiento[]>([]);
  readonly cargando = signal(true);
  readonly error = signal("");
  readonly pestana = signal<Pestana>("modulos");
  readonly busqueda = signal("");
  readonly grupo = signal("");
  readonly filtroEstado = signal<FiltroEstado>("todos");
  readonly pagina = signal(1);
  readonly porPagina = signal(10);
  readonly paginaBitacora = signal(1);
  readonly porPaginaBitacora = signal(10);
  /** El aire que sobra bajo la tabla, repartido en cada fila (px). */
  readonly extraFila = signal(0);

  private esperaAjuste: ReturnType<typeof setTimeout> | null = null;
  private ajustesSeguidos = 0;
  private ajusteEncogio = false;

  /** Cada opción con su mantenimiento, si lo tiene. */
  readonly filas = computed<FilaMantenimiento[]>(() => {
    const porClave = new Map(this.activos().map((a) => [a.clave, a] as [string, ModuloEnMantenimiento]));
    return this.opciones().map((opcion) => ({ opcion, activo: porClave.get(opcion.clave) }));
  });

  /** Los grupos, en el orden en que llegan (Solicitudes primero). */
  readonly grupos = computed(() => [...new Set(this.opciones().map((o) => o.grupo))]);

  /** Con los filtros; lo que está en mantenimiento, primero. */
  readonly filtradas = computed(() => {
    const buscado = sinAcentos(this.busqueda());
    const grupo = this.grupo();
    const estado = this.filtroEstado();
    return this.filas()
      .filter((f) => !grupo || f.opcion.grupo === grupo)
      .filter((f) => estado === "todos" || (estado === "mantenimiento") === !!f.activo)
      .filter((f) => !buscado || sinAcentos(f.opcion.nombre + " " + f.opcion.grupo).includes(buscado))
      .sort((a, b) => Number(!a.activo) - Number(!b.activo));
  });

  readonly totalPaginas = computed(() => Math.max(1, Math.ceil(this.filtradas().length / this.porPagina())));
  private readonly paginaReal = computed(() => Math.min(this.pagina(), this.totalPaginas()));
  readonly visibles = computed(() => {
    const inicio = (this.paginaReal() - 1) * this.porPagina();
    return this.filtradas().slice(inicio, inicio + this.porPagina());
  });
  readonly rango = computed(() => textoRango(this.filtradas().length, this.paginaReal(), this.porPagina(), "módulo", "módulos"));
  readonly numeroPagina = this.paginaReal;

  readonly totalPaginasBitacora = computed(() => Math.max(1, Math.ceil(this.bitacora().length / this.porPaginaBitacora())));
  readonly numeroPaginaBitacora = computed(() => Math.min(this.paginaBitacora(), this.totalPaginasBitacora()));
  readonly bitacoraVisible = computed(() => {
    const inicio = (this.numeroPaginaBitacora() - 1) * this.porPaginaBitacora();
    return this.bitacora().slice(inicio, inicio + this.porPaginaBitacora());
  });
  readonly rangoBitacora = computed(() =>
    textoRango(this.bitacora().length, this.numeroPaginaBitacora(), this.porPaginaBitacora(), "movimiento", "movimientos"));

  constructor() {
    // Cada vez que cambia lo que se pinta, se vuelven a medir las filas que caben.
    effect(() => {
      this.visibles();
      this.bitacoraVisible();
      this.pestana();
      this.programaAjuste();
    });
    inject(DestroyRef).onDestroy(() => {
      if (this.esperaAjuste) {
        clearTimeout(this.esperaAjuste);
      }
    });
  }

  ngOnInit(): void {
    this.cargar();
  }

  cargar(): void {
    this.cargando.set(true);
    this.api.catalogo().subscribe({
      next: (res) => {
        this.cargando.set(false);
        if (!res || res.codigoError) {
          this.error.set(res?.mensajeError || "No se pudo consultar el mantenimiento de módulos.");
          return;
        }
        this.error.set("");
        this.opciones.set(res.opciones || []);
        this.aplica(res);
      },
      error: () => {
        this.cargando.set(false);
        this.error.set("No se pudo consultar el mantenimiento de módulos. Revisa tu conexión e inténtalo de nuevo.");
      },
    });
  }

  /** Lo que quedó después de guardar o terminar; el guardia lo usa ya, sin esperar su minuto. */
  private aplica(res: MantenimientoModulosRes): void {
    this.activos.set(res.activos || []);
    this.bitacora.set(res.bitacora || []);
    this.estado.pon(res.activos || []);
    this.reiniciaAjuste();
  }

  cambiaPestana(pestana: Pestana): void {
    this.pestana.set(pestana);
    this.reiniciaAjuste();
  }

  cambiaBusqueda(valor: string): void {
    this.busqueda.set(valor);
    this.vuelveAlInicio();
  }

  cambiaGrupo(valor: string): void {
    this.grupo.set(valor);
    this.vuelveAlInicio();
  }

  cambiaEstado(valor: FiltroEstado): void {
    this.filtroEstado.set(valor);
    this.vuelveAlInicio();
  }

  irA(pagina: number): void {
    this.pagina.set(Math.min(Math.max(1, pagina), this.totalPaginas()));
  }

  irABitacora(pagina: number): void {
    this.paginaBitacora.set(Math.min(Math.max(1, pagina), this.totalPaginasBitacora()));
  }

  private vuelveAlInicio(): void {
    this.pagina.set(1);
    this.reiniciaAjuste();
  }

  /** Poner en mantenimiento, o cambiar el aviso de lo que ya lo está. */
  edita(fila: FilaMantenimiento): void {
    abrirDialogoMantenimiento(this.dialog, { opcion: fila.opcion, actual: fila.activo }).subscribe((res) => {
      if (!res) {
        return;
      }
      this.aplica(res);
      this.avisa(fila.activo ? `Se cambió el aviso de «${fila.opcion.nombre}».` : `«${fila.opcion.nombre}» quedó en mantenimiento.`);
    });
  }

  /** Terminar el mantenimiento, después de confirmarlo. */
  termina(fila: FilaMantenimiento): void {
    swal
      .fire({
        title: "¿Terminar el mantenimiento?",
        text: `«${fila.opcion.nombre}» vuelve a estar disponible para todos.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Terminar",
        cancelButtonText: "Cancelar",
      })
      .then((r) => {
        if (!r.isConfirmed) {
          return;
        }
        this.api.terminar(fila.opcion.clave).subscribe({
          next: (res) => {
            if (!res || res.codigoError) {
              swal.fire("No se terminó", res?.mensajeError || "Inténtalo de nuevo.", "warning");
              this.cargar();
              return;
            }
            this.aplica(res);
            this.avisa(`«${fila.opcion.nombre}» ya está disponible.`);
          },
          error: () => swal.fire("Error", "No se pudo terminar el mantenimiento. Inténtalo de nuevo.", "error"),
        });
      });
  }

  private avisa(texto: string): void {
    swal.fire({ toast: true, position: "top-end", icon: "success", title: texto, showConfirmButton: false, timer: 2600 });
  }

  /** Cuándo vuelve, la fecha y aparte la hora; null si dura hasta que alguien lo termine. */
  vuelveDe(activo: ModuloEnMantenimiento): { fecha: string; hora: string } | null {
    const m = partesDe(activo.hasta);
    return m ? { fecha: `${m[3]}/${m[2]}/${m[1]}`, hora: `a las ${m[4]}:${m[5]}` } : null;
  }

  /** «Lo puso Norman Estrada el 16/10/2026 a las 10:00». */
  puestoDe(activo: ModuloEnMantenimiento): string {
    const desde = textoHasta(activo.desde);
    if (activo.usuarioNombre) {
      return `Lo puso ${activo.usuarioNombre}${desde ? " " + desde : ""}`;
    }
    return desde ? `Desde ${desde}` : "";
  }

  accionDe(m: MovimientoMantenimiento): string {
    return ACCIONES[m.accion] || m.accion;
  }

  fechaDe(fecha: string | null | undefined): string {
    const m = partesDe(fecha);
    return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : "";
  }

  @HostListener("window:resize")
  alCambiarTamano(): void {
    this.reiniciaAjuste();
    this.programaAjuste();
  }

  /** Lo que hizo la persona (filtro, pestaña, datos nuevos) permite volver a contar las filas que caben. */
  private reiniciaAjuste(): void {
    this.ajustesSeguidos = 0;
    this.ajusteEncogio = false;
  }

  private programaAjuste(pasadas = 2): void {
    if (this.esperaAjuste) {
      clearTimeout(this.esperaAjuste);
    }
    this.esperaAjuste = setTimeout(() => {
      this.esperaAjuste = null;
      this.ajustaFilas(pasadas);
    }, 60);
  }

  /**
   * Las filas que caben hasta el final de la ventana (Norman no quiere
   * pantallas que rueden) y el aire que sobra, repartido en cada fila. Las
   * filas por página solo cambian en la primera página y como mucho tres
   * veces seguidas, para que la cuenta no brinque entre dos valores; al
   * cambiarlas, la tabla se vuelve a pintar y se mide otra vez.
   */
  private ajustaFilas(pasadas: number): void {
    const tabla = this.tabla()?.nativeElement;
    if (!tabla) {
      return;
    }
    const enBitacora = this.pestana() === "bitacora";
    const tamano = enBitacora ? this.porPaginaBitacora : this.porPagina;
    const enPrimera = (enBitacora ? this.numeroPaginaBitacora() : this.paginaReal()) === 1;
    const total = enBitacora ? this.bitacora().length : this.filtradas().length;
    const ajuste = ajustePaginado(tabla, MARGEN_INFERIOR, FILAS_MIN, FILAS_MAX, ALTO_FILA, total);
    const cuenta = cuentaEstable(tamano(), ajuste.porPagina, this.ajusteEncogio);
    if (cuenta !== tamano() && enPrimera && this.ajustesSeguidos < 3) {
      this.ajustesSeguidos++;
      this.ajusteEncogio = cuenta < tamano();
      tamano.set(cuenta);
      return;
    }
    if (ajuste.extraFila !== this.extraFila()) {
      this.extraFila.set(ajuste.extraFila);
      if (pasadas > 0) {
        this.programaAjuste(pasadas - 1);
      }
    }
  }
}
